import axios from 'axios';

// Create axios instance with default config
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || 'http://localhost:5000/api',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 seconds
});

// Request interceptor - attach token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;

      if (status === 401) {
        // Unauthorized - clear token and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }

      // Return error message from server or default message
      return Promise.reject(
        data.message || data.error || 'An error occurred'
      );
    } else if (error.request) {
      // Request was made but no response received
      return Promise.reject('No response from server. Please check your connection.');
    } else {
      // Something else happened
      return Promise.reject(error.message || 'An error occurred');
    }
  }
);

// ==========================================
// Authentication API
// ==========================================

export const authAPI = {
  // Register new user
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },

  // Login user
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },

  // Get current user
  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  // Logout user
  logout: async () => {
    const response = await api.post('/auth/logout');
    return response.data;
  },

  // Update user profile
  updateProfile: async (userData) => {
    const response = await api.put('/auth/update', userData);
    return response.data;
  },
};

// ==========================================
// Sensor Data API
// ==========================================

export const sensorAPI = {
  // Get all sensor data with pagination
  getData: async (params = {}) => {
    const { page = 1, limit = 50, startDate, endDate, deviceId } = params;
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
      ...(deviceId && { deviceId }),
    });

    const response = await api.get(`/data?${queryParams}`);
    return response.data;
  },

  // Get latest sensor reading
  getLatest: async () => {
    const response = await api.get('/data/latest');
    return response.data;
  },

  // Get sensor data statistics
  getStats: async (startDate, endDate) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await api.get(`/data/stats?${params}`);
    return response.data;
  },

  // Get single sensor reading by ID
  getById: async (id) => {
    const response = await api.get(`/data/${id}`);
    return response.data;
  },

  // Delete sensor reading (admin only)
  deleteData: async (id) => {
    const response = await api.delete(`/data/${id}`);
    return response.data;
  },

  // Delete multiple readings by date range (admin only)
  deleteBulk: async (startDate, endDate) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await api.delete(`/data?${params}`);
    return response.data;
  },
};

// ==========================================
// Helper Functions
// ==========================================

// Set authentication token
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('token', token);
  } else {
    localStorage.removeItem('token');
  }
};

// Get authentication token
export const getAuthToken = () => {
  return localStorage.getItem('token');
};

// Check if user is authenticated
export const isAuthenticated = () => {
  return !!getAuthToken();
};

export default api;
